import React, { useState, useEffect } from 'react';
import authService from '../../services/auth.service';
import {
    Description as DocumentTextIcon,
    Storage as ServerIcon,
    CalendarToday as CalendarIcon,
    DataUsage as CircleStackIcon,
    TrendingUp as ArrowUpIcon,
    TrendingDown as ArrowDownIcon,
    AccessTime as ClockIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as XCircleIcon,
    NoteAdd as DocumentPlusIcon,
    Settings as CogIcon,
    People as UsersIcon
} from '@mui/icons-material';

interface Stats {
    totalFiles: number;
    totalFilesChange: number;
    syncedFiles: number;
    totalSize: number;
    activeMachines: number;
    activeMachinesChange: number;
    documentsToday: number;
    documentsTodayChange: number;
    storageUsed: number;
    storageTotal: number;
    processingPending: number;
    processingCompleted: number;
    processingFailed: number;
}

interface ActivityItem {
    id: string;
    type: 'upload' | 'process' | 'delete' | 'login' | 'sync';
    title: string;
    description: string;
    timestamp: Date;
    userId?: string;
    userName?: string;
    machineId?: string;
    status?: 'success' | 'error' | 'warning';
}

interface MachineStatus {
    machineId: string;
    machineName: string;
    status: 'active' | 'offline' | 'interrupted';
    lastSeen: Date;
    filesCount: number;
    storageUsed: number;
    syncProgress?: number;
}

const EnhancedDashboard: React.FC = () => {
    const [stats, setStats] = useState<Stats>({
        totalFiles: 0,
        totalFilesChange: 0,
        syncedFiles: 0,
        totalSize: 0,
        activeMachines: 0,
        activeMachinesChange: 0,
        documentsToday: 0,
        documentsTodayChange: 0,
        storageUsed: 0,
        storageTotal: 5497558138880, // 5TB
        processingPending: 0,
        processingCompleted: 0,
        processingFailed: 0
    });
    const [activities, setActivities] = useState<ActivityItem[]>([]);
    const [machines, setMachines] = useState<MachineStatus[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
        // Set up real-time updates
        const interval = setInterval(fetchDashboardData, 30000); // Update every 30 seconds
        return () => clearInterval(interval);
    }, []);

    const fetchDashboardData = async () => {
        try {
            const api = authService.getApiInstance();
            const [statsRes, activitiesRes, machinesRes] = await Promise.all([
                api.get('/dashboard/stats'),
                api.get('/dashboard/activities?limit=10'),
                api.get('/machines/status')
            ]);

            setStats(statsRes.data);
            setActivities(activitiesRes.data);
            setMachines(machinesRes.data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatTimeAgo = (date: Date): string => {
        const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);
        if (seconds < 60) return 'just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    const getActivityIcon = (type: string) => {
        switch (type) {
            case 'upload':
                return <DocumentPlusIcon sx={{ width: 20, height: 20 }} />;
            case 'process':
                return <CogIcon sx={{ width: 20, height: 20 }} />;
            case 'delete':
                return <XCircleIcon sx={{ width: 20, height: 20 }} />;
            case 'login':
                return <UsersIcon sx={{ width: 20, height: 20 }} />;
            case 'sync':
                return <ServerIcon sx={{ width: 20, height: 20 }} />;
            default:
                return <DocumentTextIcon sx={{ width: 20, height: 20 }} />;
        }
    };

    const getActivityColor = (type: string) => {
        switch (type) {
            case 'upload':
                return 'bg-blue-100 text-blue-600';
            case 'process':
                return 'bg-purple-100 text-purple-600';
            case 'delete':
                return 'bg-red-100 text-red-600';
            case 'login':
                return 'bg-green-100 text-green-600';
            case 'sync':
                return 'bg-orange-100 text-orange-600';
            default:
                return 'bg-gray-100 text-gray-600';
        }
    };

    const getMachineStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800';
            case 'offline':
                return 'bg-gray-100 text-gray-800';
            case 'interrupted':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const storagePercentage = (stats.storageUsed / stats.storageTotal) * 100;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-600 mt-2">
                    Welcome back! Here's what's happening with your documents today.
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Total Documents */}
                <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Documents</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                {stats.totalFiles.toLocaleString()}
                            </p>
                            <div className={`flex items-center mt-2 text-sm ${
                                stats.totalFilesChange >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                                {stats.totalFilesChange >= 0 ? (
                                    <ArrowUpIcon sx={{ width: 16, height: 16, mr: 0.5 }} />
                                ) : (
                                    <ArrowDownIcon sx={{ width: 16, height: 16, mr: 0.5 }} />
                                )}
                                <span>{Math.abs(stats.totalFilesChange)}% from last month</span>
                            </div>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <DocumentTextIcon sx={{ width: 32, height: 32, color: '#2563eb' }} />
                        </div>
                    </div>
                </div>

                {/* Active Machines */}
                <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Active Machines</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                {stats.activeMachines}
                            </p>
                            <div className={`flex items-center mt-2 text-sm ${
                                stats.activeMachinesChange >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                                {stats.activeMachinesChange >= 0 ? (
                                    <ArrowUpIcon sx={{ width: 16, height: 16, mr: 0.5 }} />
                                ) : (
                                    <ArrowDownIcon sx={{ width: 16, height: 16, mr: 0.5 }} />
                                )}
                                <span>{Math.abs(stats.activeMachinesChange)} new this week</span>
                            </div>
                        </div>
                        <div className="p-3 bg-green-100 rounded-lg">
                            <ServerIcon sx={{ width: 32, height: 32, color: '#16a34a' }} />
                        </div>
                    </div>
                </div>

                {/* Documents Today */}
                <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Documents Today</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                {stats.documentsToday.toLocaleString()}
                            </p>
                            <div className="flex items-center mt-2 text-sm text-orange-600">
                                <ClockIcon sx={{ width: 16, height: 16, mr: 0.5 }} />
                                <span>{stats.documentsTodayChange} in last hour</span>
                            </div>
                        </div>
                        <div className="p-3 bg-orange-100 rounded-lg">
                            <CalendarIcon sx={{ width: 32, height: 32, color: '#ea580c' }} />
                        </div>
                    </div>
                </div>

                {/* Storage Used */}
                <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Storage Used</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                {formatFileSize(stats.storageUsed)}
                            </p>
                            <div className={`flex items-center mt-2 text-sm ${
                                storagePercentage > 80 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                                <CircleStackIcon sx={{ width: 16, height: 16, mr: 0.5 }} />
                                <span>{storagePercentage.toFixed(1)}% of capacity</span>
                            </div>
                        </div>
                        <div className={`p-3 rounded-lg ${
                            storagePercentage > 80 ? 'bg-red-100' : 'bg-purple-100'
                        }`}>
                            <CircleStackIcon sx={{ 
                                width: 32, 
                                height: 32, 
                                color: storagePercentage > 80 ? '#dc2626' : '#9333ea' 
                            }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Activity */}
                <div className="lg:col-span-2 bg-white rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                                View All
                            </button>
                        </div>
                    </div>
                    <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                        {activities.map((activity) => (
                            <div key={activity.id} className="flex items-start space-x-3">
                                <div className={`p-2 rounded-full ${getActivityColor(activity.type)}`}>
                                    {getActivityIcon(activity.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                                    <p className="text-sm text-gray-500">{activity.description}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {formatTimeAgo(activity.timestamp)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Quick Actions & Machine Status */}
                <div className="space-y-6">
                    {/* Quick Actions */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
                        <div className="space-y-3">
                            <button className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-500 transition-colors group">
                                <div className="flex items-center">
                                    <DocumentPlusIcon sx={{ width: 24, height: 24, color: '#2563eb', mr: 1.5 }} />
                                    <div>
                                        <p className="font-medium text-gray-900">Upload Documents</p>
                                        <p className="text-sm text-gray-500">Add new files to the system</p>
                                    </div>
                                </div>
                            </button>
                            <button className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-500 transition-colors group">
                                <div className="flex items-center">
                                    <CogIcon sx={{ width: 24, height: 24, color: '#2563eb', mr: 1.5 }} />
                                    <div>
                                        <p className="font-medium text-gray-900">Process Queue</p>
                                        <p className="text-sm text-gray-500">
                                            {stats.processingPending} documents pending
                                        </p>
                                    </div>
                                </div>
                            </button>
                            <button className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-500 transition-colors group">
                                <div className="flex items-center">
                                    <UsersIcon sx={{ width: 24, height: 24, color: '#2563eb', mr: 1.5 }} />
                                    <div>
                                        <p className="font-medium text-gray-900">Manage Users</p>
                                        <p className="text-sm text-gray-500">Add or modify user access</p>
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Machine Status */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Machine Status</h2>
                        <div className="space-y-3">
                            {machines.slice(0, 3).map((machine) => (
                                <div key={machine.machineId} className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <ServerIcon sx={{ width: 20, height: 20, color: '#9ca3af' }} />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {machine.machineName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {machine.filesCount} files • {formatFileSize(machine.storageUsed)}
                                            </p>
                                        </div>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                        getMachineStatusColor(machine.status)
                                    }`}>
                                        {machine.status}
                                    </span>
                                </div>
                            ))}
                            <button className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium pt-2">
                                View All Machines
                            </button>
                        </div>
                    </div>

                    {/* Storage Overview */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Storage Overview</h2>
                        <div className="space-y-3">
                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-gray-600">Used Space</span>
                                    <span className="font-medium text-gray-900">
                                        {formatFileSize(stats.storageUsed)} / {formatFileSize(stats.storageTotal)}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                        className={`h-2 rounded-full transition-all duration-300 ${
                                            storagePercentage > 80 
                                                ? 'bg-red-500' 
                                                : storagePercentage > 60 
                                                ? 'bg-yellow-500' 
                                                : 'bg-blue-500'
                                        }`}
                                        style={{ width: `${storagePercentage}%` }}
                                    />
                                </div>
                            </div>
                            <div className="pt-2 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Documents</span>
                                    <span className="text-gray-900">{stats.totalFiles.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Processing Queue</span>
                                    <span className="text-gray-900">{stats.processingPending}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Document Processing Status */}
            <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Document Processing</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <ClockIcon sx={{ width: 32, height: 32, color: '#ca8a04', mx: 'auto', mb: 1 }} />
                        <p className="text-2xl font-bold text-gray-900">{stats.processingPending}</p>
                        <p className="text-sm text-gray-600">Pending</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <CheckCircleIcon sx={{ width: 32, height: 32, color: '#16a34a', mx: 'auto', mb: 1 }} />
                        <p className="text-2xl font-bold text-gray-900">{stats.processingCompleted}</p>
                        <p className="text-sm text-gray-600">Completed Today</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                        <XCircleIcon sx={{ width: 32, height: 32, color: '#dc2626', mx: 'auto', mb: 1 }} />
                        <p className="text-2xl font-bold text-gray-900">{stats.processingFailed}</p>
                        <p className="text-sm text-gray-600">Failed</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnhancedDashboard;